<template>
  <div
    class="form-toggle-list-element-wrapper w-100"
    @dragover.prevent="handleDrogOver"
    @drop="handleDrop"
    ref="toggleElementRef"
    @dragleave="handleDragLeave"
    :class="{ 'open-toggle-list': isCollapseOpen, 'preview-mode': isPreview }"
  >
    <div
      class="toggle-list-container w-100"
      :class="{ 'show-drop-zone': isShowDropZone }"
    >
      <div
        class="d-flex toggle-handle align-items-center justify-content-between"
        :data-drag-handle="`label-${element.id}`"
      >
        <span
          :id="`label-${element.id}`"
          :style="labelStyle"
          class="label fs-6 fw-bold"
          >{{ element.label }}</span
        >
        <img
          @dragover.prevent.stop
          @click.stop
          v-b-toggle="`toggle-list-${element.id}`"
          src="@/assets/img/toggle-chevron-down-icon.svg"
        />
      </div>

      <b-collapse
        v-model="isCollapseOpen"
        :id="`toggle-list-${element.id}`"
        ref="collapseRef"
        @input="onCollapseChange"
      >
        <transition-group
          v-if="getToggleListElements?.length"
          name="fade-move"
          tag="div"
          class="item-wrapper d-flex flex-column"
        >
          <FormBuilderElement
            v-for="(element, index) in getToggleListElements"
            :key="element.id"
            :element="element"
            :elements="elements"
            :isPreview="isPreview"
            :brandedStyle="brandedStyle"
            :rowIndex="rowIndex"
            :colIndex="colIndex"
            :tabIndex="tabIndex"
            :isActivePropsElement="isActivePropsElement"
            :draggable="draggable"
            :dragItemId="dragItemId"
            :tabStructure="tabStructure"
            :tabData="tabData"
            :isDragInsideGrid="isDragInsideGrid"
            :toggleListState="toggleListState"
            :isAskAiModalVisible="isAskAiModalVisible"
            :layoutSetting="layoutSetting"
            :formSettings="formSettings"
            :brandedSubmitBtnStyle="brandedSubmitBtnStyle"
            @editElement="onElementEdit"
            :dragPosition="getDragPostion(index)"
            @dragstart="onElementDragStart($event, element)"
            @dragover="onElementDragOver($event, index, element)"
            @drop="onElementDrop($event, element.id)"
            @dragleave="onElementDragLeave($event, element)"
            @dragend="onDragEnd"
            @removeElement="onRemoveElement"
            @duplicateElement="onDuplicateElement"
            @insertNewElement="onInsertNewElement"
            @setDragItemId="setDragItemId"
            @tabButtonSubmit="(event) => $emit('tabButtonSubmit', event)"
          />
        </transition-group>
      </b-collapse>
    </div>
    <hr class="bottom-line" />
  </div>
</template>

<script>
import {
  moveElementInTabStructure,
  findElementInTab,
  setDragItemImage,
  removeDragItemImage,
} from '@/utils/helpers/FormBuilderHelper';

export default {
  name: 'FormToggleListElement',
  data() {
    return {
      dragOverIndex: null,
      dragPosition: null,
      isAllowToDropOuterItem: false,
      childrenRect: null,
    };
  },
  props: {
    element: {
      type: Object,
      required: true,
    },
    elements: {
      type: Array,
      required: true,
    },
    isActivePropsElement: {
      type: Function,
      default: () => false,
    },
    isPreview: { type: Boolean, default: false },
    draggable: {
      type: Boolean,
      default: false,
    },
    dragItemId: {
      type: Number,
      default: null,
    },
    brandedStyle: { type: Object, default: () => {} },
    labelStyle: { type: Object, default: () => {} },
    tabStructure: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    tabData: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    rowIndex: {
      type: [Number, String],
      required: true,
    },
    colIndex: {
      type: [Number, String],
      required: true,
    },
    tabIndex: {
      type: [Number, String],
      required: true,
    },
    isDragInsideGrid: {
      type: Boolean,
      default: () => false,
    },
    layoutSetting: {
      type: Object,
      default: () => ({}),
    },
    formSettings: {
      type: Object,
      default: () => ({}),
    },
    brandedSubmitBtnStyle: {
      type: Object,
      default: () => ({}),
    },
    toggleListState: {
      type: Object,
      default: () => {},
    },
    isAskAiModalVisible: { type: Boolean, default: false },
  },
  methods: {
    onRemoveElement(element) {
      this.$emit('removeElement', element);
    },
    onDuplicateElement(element) {
      this.$emit('duplicateElement', element);
    },
    onInsertNewElement(elementId, position) {
      this.$emit('insertNewElement', elementId, position);
    },

    onElementEdit(element) {
      if (!this.isPreview) this.$emit('editElement', element || this.element);
    },
    setDragItemId(id) {
      this.$emit('setDragItemId', id);
    },
    handleDrogOver(event) {
      if (
        this.dragOverIndex !== null ||
        this.isPreview ||
        !this.dragItemId ||
        this.dragItemId === this.element.id ||
        (this.getToggleDragItemIndex && this.getToggleDragItemIndex !== -1) ||
        !this.isDropAllowed(this.element.id)
      )
        return;
      if (this.getToggleListElements.length) {
        const lastToggleListItem =
          this.getToggleListElements[this.getToggleListElements.length - 1];
        if (lastToggleListItem && lastToggleListItem.id === this.dragItemId) {
          this.isAllowToDropOuterItem = false;
          return;
        }
      }
      event.dataTransfer.dropEffect = 'move';
      const bounds = event.currentTarget.getBoundingClientRect();
      const mouseY = event.clientY;
      const offsetTop = Math.round(mouseY - bounds.top);
      const height = bounds.height;
      this.isAllowToDropOuterItem = offsetTop >= 27 && offsetTop <= height - 2;
    },
    handleDrop(event) {
      if (this.isPreview || !this.isAllowToDropOuterItem || !this.draggable)
        return;
      event.stopPropagation();

      if (!this.dragItemId || this.dragItemId === this.element.id)
        return this.setDragItemId(null);
      const updatedTabList = moveElementInTabStructure({
        data: this.tabStructure.tabs,
        dragId: this.dragItemId,
        rowIndex: this.rowIndex,
        colIndex: this.colIndex,
        dropTabIndex: this.tabIndex,
        dropId: this.element.id,
      });

      if (updatedTabList) this.$set(this.tabStructure, 'tabs', updatedTabList);
      this.setDragItemId(null);
      this.onDragEnd();
      this.$emit('dragend');
    },
    handleDragLeave() {
      this.onDragEnd();
    },
    getDragPostion(index) {
      return this.dragOverIndex === index ? this.dragPosition : null;
    },
    onElementDragStart(event, element) {
      if (this.isPreview || !this.draggable) return;
      event.stopPropagation();
      this.setDragItemId(element.id);
      setDragItemImage(event);

      /* read children area once per drag session */
      this.$nextTick(() => {
        const wrapper = this.$el.querySelector('.item-wrapper');
        this.childrenRect = wrapper ? wrapper.getBoundingClientRect() : null;
      });
    },
    onElementDragOver(event, index, element) {
      if (this.isPreview || !this.dragItemId || !this.draggable) return;
      this.dragOverIndex = index;
      const bounds = event.currentTarget.getBoundingClientRect();
      const mouseY = event.clientY;
      const offsetTop = Math.round(mouseY - bounds.top);
      const height = bounds.height;

      if (
        this.isAllowToDropOuterItem ||
        (offsetTop >= 27 + 15 &&
          offsetTop <= height - 17 &&
          ['wrapper', 'togglelist'].includes(element.type))
      ) {
        this.dragPosition = null;
        return;
      }
      const position = offsetTop <= height * 0.5 ? 'top' : 'bottom';
      if (
        !this.isDropAllowed(element.id, position) ||
        (this.getToggleDragItemIndex !== -1 &&
          (index === this.getToggleDragItemIndex ||
            (position === 'bottom' &&
              index === this.getToggleDragItemIndex - 1) ||
            (position === 'top' && index === this.getToggleDragItemIndex + 1)))
      ) {
        this.dragPosition = null;
      } else {
        this.dragPosition = position;
      }
    },
    onElementDrop(event, elementId) {
      if (
        this.isPreview ||
        !this.dragItemId ||
        !this.dragPosition ||
        !this.draggable
      )
        return;
      event.stopPropagation();
      const updatedTabList = moveElementInTabStructure({
        data: this.tabStructure.tabs,
        dragId: this.dragItemId,
        dropId: elementId,
        position: this.dragPosition,
        rowIndex: this.rowIndex,
        colIndex: this.colIndex,
        dropTabIndex: this.tabIndex,
      });

      if (updatedTabList) this.$set(this.tabStructure, 'tabs', updatedTabList);
      this.setDragItemId(null);
      this.onDragEnd();
      this.$emit('dragend');
    },
    onElementDragLeave(event, element) {
      event.stopPropagation();
      if (this.getToggleListElements.length) {
        const firstToggleListItem = this.getToggleListElements[0];

        const srcElement = event.srcElement;
        const fromElement = event.fromElement;
        const isDraggingOut =
          fromElement?.closest('.form-toggle-list-element-wrapper') !==
          this.$refs.toggleElementRef;

        if (
          isDraggingOut ||
          (firstToggleListItem &&
            firstToggleListItem.id === element.id &&
            srcElement?.classList?.contains('form-field') &&
            !(
              srcElement?.classList?.contains('form-field') &&
              fromElement?.classList?.contains('form-field') &&
              fromElement.id != srcElement.id
            ) &&
            !event.target.contains(event.fromElement))
        ) {
          this.onDragEnd();
        }
      }
    },
    onDragEnd() {
      this.dragOverIndex = null;
      this.dragPosition = null;
      this.isAllowToDropOuterItem = false;
      this.childrenRect = null;
      removeDragItemImage();
    },
    isDropAllowed(elementId, position = null) {
      return (
        this.dragItemId &&
        moveElementInTabStructure({
          data: this.tabStructure.tabs,
          dragId: this.dragItemId,
          dropId: elementId,
          rowIndex: this.rowIndex,
          colIndex: this.colIndex,
          dropTabIndex: this.tabIndex,
          position: position,
          checkIsAllow: true,
        })
      );
    },
    onCollapseChange(isOpen) {
      this.$emit('onToggleListStateChange', this.element.id, isOpen);
    },
  },
  computed: {
    getToggleListElements() {
      const activeTab = this.tabStructure.tabs[this.tabData.activeTabIndex];
      const toggleElement = findElementInTab(activeTab, this.element.id);
      return (toggleElement?.childs || []).map((child) =>
        this.elements.find((element) => element.id === child.elementId)
      );
    },
    getToggleDragItemIndex() {
      const elements = this.getToggleListElements;
      return elements.findIndex((el) => el.id === this.dragItemId);
    },
    isShowDropZone() {
      return (
        !this.isPreview &&
        this.isAllowToDropOuterItem &&
        this.isDropAllowed(this.element.id)
      );
    },
    isCollapseOpen: {
      get() {
        return this.toggleListState[this.element.id] || false;
      },
      set(value) {
        this.$set(this.toggleListState, this.element.id, value);
        this.$emit('update:toggleListState', this.toggleListState);
      },
    },
  },
  components: { FormBuilderElement: () => import('../FormBuilderElement.vue') },
};
</script>

<style scoped>
.form-toggle-list-element-wrapper .toggle-list-container {
  min-height: 58px;
}

.form-toggle-list-element-wrapper.preview-mode
  .toggle-list-container
  .form-toggle-list-element-wrapper {
  padding-right: 15px;
}
.form-toggle-list-element-wrapper .toggle-list-container .toggle-handle {
  height: 27px;
}
.form-toggle-list-element-wrapper .toggle-list-container .toggle-handle img {
  padding: 7px 5px;
  z-index: 52;
}

.form-toggle-list-element-wrapper.preview-mode
  .toggle-list-container
  > .toggle-handle {
  margin-bottom: 31px;
}

.form-toggle-list-element-wrapper
  .toggle-list-container
  .collapse:has(.item-wrapper) {
  padding-bottom: 15px;
}
.form-toggle-list-element-wrapper.preview-mode
  .toggle-list-container
  .collapse {
  padding: 0;
}
.form-toggle-list-element-wrapper
  .toggle-list-container
  .toggle-handle
  img[role='button']:focus {
  outline: none;
}
.form-toggle-list-element-wrapper .toggle-list-container.show-drop-zone::after {
  content: '';
  position: absolute;
  left: 12px;
  right: 12px;
  height: 30px;
  bottom: 16px;
  background-color: #86c3f4;
  opacity: 0.5;
}

.form-toggle-list-element-wrapper.open-toggle-list
  > .toggle-list-container:has(.item-wrapper).show-drop-zone::after {
  left: 0;
  height: 4px;
  bottom: 29px;
  opacity: 1;
}
.form-toggle-list-element-wrapper .toggle-list-container img.not-collapsed {
  transform: rotate(180deg);
}
.form-toggle-list-element-wrapper .toggle-list-container .label {
  font-family: 'OpenSans-Bold';
  color: #000000;
  margin: 0px;
  line-height: 27px;
  letter-spacing: -0.02em;
  text-align: left;
  padding-left: 12px;
}
.form-toggle-list-element-wrapper.preview-mode .toggle-handle .label {
  padding-left: 0 !important;
}

.form-toggle-list-element-wrapper .bottom-line {
  margin: 0;
  color: #d9dee3;
  opacity: 1;
  border-width: 0.5px;
  margin-left: 12px;
  width: calc(100% - 12px);
}
.form-toggle-list-element-wrapper.preview-mode .bottom-line {
  margin-left: 0 !important;
  width: 100% !important;
}

/* Transition  animations */
.fade-move-enter-active,
.fade-move-leave-active {
  transition: all 0.25s ease;
}
.fade-move-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
.fade-move-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}
.fade-move-move {
  transition: transform 0.3s ease;
}
</style>
